import { Injectable, inject } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { zipSync, unzipSync, strToU8, strFromU8 } from 'fflate';
import {
	IProjectBackupHandler,
	ProjectBackupService,
	ProjectBackupAttachmentMeta,
	parseBackup,
	applyBackupMigrations,
	APP_ID,
	CURRENT_DATA_VERSION,
	MigrationService,
	bereichDocsToLegacyProjectShape,
	FileExportService,
	ConsoleErrorService
} from '@luzula/luzula-lib';
import { DataService } from '../data.service';
import { ProjectService } from '../project.service';
import { AufnahmePunktDocument } from '../data-model';
import { ImportWizardComponent } from './import-wizard.component';
import { ZeichnungenService } from '../zeichnungen.service';
import { BereicheService } from '../bereiche.service';

/**
 * Setzt die Zeichnungs-Features aus dem Store (bpweb-zeichnungen) je Layer-ID ins
 * Layer-Skelett ein — pure & testbar. Ab S5 trägt das Projekt-Doc nur noch das
 * Skelett; so enthält das Backup trotzdem alle Geometrien. Layer, die der Store
 * nicht kennt, behalten ihren Doc-Stand (Alt-Backup-/Pre-Migration-Fallback).
 *
 * **Die Zeichnungen-Datenbank ist die führende Quelle.** Ebenen, die nur dort
 * stehen, kommen mit ins Backup — hinten angehängt, damit die Reihenfolge des
 * Skeletts erhalten bleibt. Vorher richtete sich das Backup allein nach dem
 * Skelett am Projekt-Doc, und das kann hinterherhinken (es wird beim Abgleich
 * eigens zusammengeführt): Eine vierte Zeichen-Ebene fehlte im Backup komplett,
 * obwohl ihre Zeichnungen in der Datenbank lagen (Nutzer-Befund 03.09.2026,
 * Projekt „Baumgarten 26").
 */
export function rehydrateDrawingFeatures(
	skeleton: any[] | null | undefined,
	storeLayers: { id: string; name?: string; geojson?: { features?: any[] } }[]
): any[] | null | undefined {
	const store = storeLayers ?? [];
	/** Eine Ebene aus der Datenbank in die Backup-Form bringen. */
	const alsEbene = (l: any) => ({
		visible: true,
		...l,
		geojson: { type: 'FeatureCollection', features: l?.geojson?.features ?? [] },
	});

	// Ohne Skelett zählt allein, was in der Datenbank liegt.
	if (!Array.isArray(skeleton)) return store.length ? store.map(alsEbene) : skeleton;

	const nachId = new Map(store.map(l => [l.id, l]));
	const uebernommen = new Set<string>();
	const ebenen = skeleton.map(l => {
		const ausDb = nachId.get(l.id);
		if (!ausDb) return l; // Layer nicht im Store → Doc-Stand behalten
		uebernommen.add(l.id);
		return { ...l, geojson: { type: 'FeatureCollection', features: ausDb.geojson?.features ?? [] } };
	});

	for (const l of store) {
		if (uebernommen.has(l.id)) continue;
		ebenen.push(alsEbene(l));
	}
	return ebenen;
}

/**
 * Zählt die im Backup enthaltenen „geladenen Layer" für die Erfolgs-Toast-
 * Meldung — die Summe aller Karten-Overlays, analog zu den Badges im
 * Layer-Panel:
 *   • WMS-/Karten-Overlays aus `mapDefinition.layers` OHNE die Basiskarte (`isBase`)
 *   • Geodaten-Dateien (binäre Attachments, `attachmentCount`)
 *   • Zeichnungslayer (`zeichnungslayer.length`)
 *   • Punkte-Layer (+1, sobald das Projekt überhaupt Punkte hat)
 *
 * Geodaten landen als Attachments (nicht in `mapDefinition.layers`), darum
 * werden sie separat über `attachmentCount` gezählt — kein Doppelzählen.
 * Pure & testbar.
 */
export function countLoadedLayers(
	project: any,
	attachmentCount: number,
	pointCount: number
): number {
	const mapLayers: any[] = Array.isArray(project?.mapDefinition?.layers) ? project.mapDefinition.layers : [];
	const wmsOverlays = mapLayers.filter((l: any) => !l?.isBase).length;
	const drawingLayers = Array.isArray(project?.zeichnungslayer) ? project.zeichnungslayer.length : 0;
	const pointsLayer = pointCount > 0 ? 1 : 0;
	return wmsOverlays + attachmentCount + drawingLayers + pointsLayer;
}

/**
 * bpweb-Implementierung von {@link IProjectBackupHandler}.
 *
 * Backup-Format v2 = ZIP-Bündel: `backup.json` (volles Projekt-Doc inkl.
 * Bereiche/Zeichnungen/Layer + Punkte + Migrationen + Attachment-Manifest) plus
 * `attachments/<name>` (binäre Geodaten). v1 (reines JSON) bleibt restore-lesbar.
 *
 * - `createAndDownloadBackup`: liest das VOLLE Projekt-Doc (`getOne`), sammelt
 *   die Punkte via `DataService.queryIndex` (Filter `header.projects[].id`) und
 *   die binären Projekt-Attachments, erzeugt das Backup via
 *   `ProjectBackupService` und speichert die ZIP über `FileExportService`
 *   (Speicherort-Dialog auf dem Desktop, Teilen auf Mobil, sonst Download).
 * - `openRestoreWizard`: erkennt ZIP vs. JSON, parst `backup.json` strikt
 *   (`parseBackup` mit App-Match), migriert Punkte über
 *   `applyBackupMigrations` auf `CURRENT_DATA_VERSION`, öffnet den
 *   `ImportWizardComponent` im `'restore'`-Modus (Geodaten-Blobs werden
 *   durchgereicht). Returnt die wiederhergestellte Projekt-ID (oder `null`).
 */
@Injectable({ providedIn: 'root' })
export class ProjectBackupHandlerService implements IProjectBackupHandler {
	private readonly modalService = inject(NgbModal);
	private readonly toastr = inject(ToastrService);
	private readonly ces = inject(ConsoleErrorService);
	private readonly dataService = inject(DataService);
	private readonly projectService = inject(ProjectService);
	private readonly backupService = inject(ProjectBackupService);
	private readonly migrationService = inject(MigrationService);
	private readonly appId = inject(APP_ID);
	private readonly currentVersion = inject(CURRENT_DATA_VERSION);
	private readonly zeichnungen = inject(ZeichnungenService);
	private readonly bereiche = inject(BereicheService);
	private readonly fileExport = inject(FileExportService);

	async createAndDownloadBackup(projectId: string): Promise<void> {
		if (!projectId) return;

		// Sofort-Feedback: das Sammeln/Zippen kann (viele Punkte + Attachments) ein
		// paar Sekunden dauern, bevor der Browser-Download startet. Ein persistenter
		// „wird erstellt"-Toast gibt unmittelbar Rueckmeldung und wird im finally
		// wieder entfernt (Erfolg/Fehler-Toast tritt an seine Stelle).
		let progress: { toastId: number } | null = this.toastr.info('Sammle Projektdaten & packe ZIP …', 'Backup wird erstellt', {
			disableTimeOut: true,
			tapToDismiss: false
		});
		try {
			// VOLLES Projekt-Doc lesen (nicht das getrimmte projectList-Summary) —
			// nur so sind regions/zeichnungslayer/mapDefinition + Attachment-Stubs
			// (content_type/length) im Backup. Das Listen-Doc traegt diese Felder nicht.
			let project: any;
			try {
				project = await this.projectService.getOne(projectId);
			} catch (e: any) {
				this.toastr.error('Projekt konnte nicht geladen werden: ' + (e?.message ?? 'unbekannt'));
				return;
			}
			if (!project) {
				this.toastr.error('Projekt nicht gefunden.');
				return;
			}

			// S1: Zeichnungs-Geometrien aus der dedizierten DB ins Doc rehydrieren,
			// damit das Backup auch dann alle Features enthält, wenn das Projekt-Doc
			// (ab S5) nur noch das Layer-Skelett trägt. listProject liest die lokale
			// Replik (kein Request-Burst, IP-Regel). Fällt der Store aus, bleibt der Doc-Stand.
			try {
				await this.zeichnungen.openProject(projectId);
				const storeLayers = await this.zeichnungen.listProject(projectId);
				project.zeichnungslayer = rehydrateDrawingFeatures(project.zeichnungslayer, storeLayers);
			} catch (e) {
				console.warn('[Backup] Zeichnungen-Store-Rehydrierung fehlgeschlagen, nutze Doc-Stand:', e);
				this.ces.melde('[Backup] Zeichnungen nur aus dem Doc-Stand', e);
			}

			// P5 (PLAN-bereiche-db.md): Bereiche aus der dedizierten Bereiche-DB in
			// die legacy Doc-Form rehydrieren — das Backup bleibt abwärtskompatibel
			// (alte App-Versionen lesen es) und der Restore konvertiert zurück in den
			// Store. Bei migrierten Projekten sind die Doc-Keys sonst veraltet.
			try {
				if (project.bereicheMigrated) {
					await this.bereiche.openProject(projectId);
					const bereichDocs = await this.bereiche.listProject(projectId);
					const shape = bereichDocsToLegacyProjectShape(bereichDocs);
					project.regions = shape.regions;
					project.projectPoints = {
						...(project.projectPoints ?? {}),
						regionNames: shape.regionNames,
						regionFilters: shape.regionFilters
					};
				}
				// Flag NIE mit exportieren: ein Restore als neues Projekt soll die
				// P4-Auto-Migration durchlaufen (seedet die Bereiche-DB aus den Doc-Keys).
				delete project.bereicheMigrated;
			} catch (e) {
				console.warn('[Backup] Bereiche-Store-Rehydrierung fehlgeschlagen, nutze Doc-Stand:', e);
				this.ces.melde('[Backup] Bereiche nur aus dem Doc-Stand', e);
			}

			// Alle Punkte laden + nach header.projects[].id === projectId filtern.
			// `header.projects` ist die Single-Source fuer die Projekt-Zuordnung
			// (data-model.ts). closedProjects werden NICHT ins Backup gezogen —
			// nur aktive Member-Punkte gehoeren zum Projekt.
			const result = await this.dataService.queryIndex(true);
			const allPoints: AufnahmePunktDocument[] = result?.rows
				? result.rows.map((row: any) => row.doc as AufnahmePunktDocument)
				: [];
			const projectPoints = allPoints.filter(
				p => p?.header?.projects?.some((ref: any) => ref?.id === projectId) ?? false
			);

			// Defensive Normalisierung: lokale Punkte koennen aus dem PouchDB-Cache
			// noch eine alte Schema-Version tragen (z.B. wenn die App-Initial-
			// Migration nicht griff). Vor dem Backup zwingen wir alle Punkte auf
			// die aktuelle CURRENT_DATA_VERSION — die App-internen MigrationDefs
			// haben ihre Closures intakt, im Backup waeren sie nach `fn.toString()`
			// nicht mehr ausfuehrbar. Das Backup ist anschliessend konsistent
			// (schemaVersion entspricht ALLEN Punkt-Versionen).
			const migratedPoints = projectPoints.map(
				p => this.migrationService.migrate(p, this.currentVersion) as AufnahmePunktDocument
			);

			// Binäre Geodaten-Attachments einsammeln. Sequenziell (typischerweise nur
			// wenige Dateien) — bewusst KEIN paralleler Request-Burst gegen den Server
			// (IP-Sperre-Regel). Manifest beschreibt jede Datei fuer den Restore.
			const attManifest: ProjectBackupAttachmentMeta[] = [];
			const zipFiles: Record<string, Uint8Array> = {};
			const attMeta = (project._attachments ?? {}) as Record<string, any>;
			for (const name of Object.keys(attMeta)) {
				try {
					const blob = await this.projectService.getProjectAttachment(projectId, name);
					if (!blob) continue;
					const u8 = new Uint8Array(await blob.arrayBuffer());
					zipFiles[`attachments/${name}`] = u8;
					attManifest.push({
						name,
						contentType: attMeta[name]?.content_type ?? blob.type ?? 'application/octet-stream',
						size: u8.byteLength
					});
				} catch (e) {
					// Eine Sicherung mit fehlendem Anhang sieht aus wie eine vollstaendige.
					console.warn('[Backup] Attachment konnte nicht gelesen werden:', name, e);
					this.ces.melde('[Backup] Anhang fehlt in der Sicherung', e);
				}
			}

			const backup = this.backupService.createBackup({
				project,
				points: migratedPoints,
				attachments: attManifest
			});
			zipFiles['backup.json'] = strToU8(JSON.stringify(backup, null, 2));

			const fileName = this.backupService.suggestFileName(project.name ?? '', backup.exportedAt);
			let blob: Blob;
			try {
				// Synchrones Zippen: fflates async-Variante nutzt Worker (im Test-Env
				// flakey); fuer uebliche Backup-Groessen ist zipSync schnell genug.
				const zipped = zipSync(zipFiles, { level: 6 });
				// Cast: fflate liefert Uint8Array<ArrayBufferLike>; BlobPart verlangt
				// seit TS-5.7-lib.dom <ArrayBuffer> (Laufzeit ist hier nie SharedArrayBuffer).
				blob = new Blob([zipped as BlobPart], { type: 'application/zip' });
			} catch (e: any) {
				this.toastr.error('Backup-ZIP konnte nicht erstellt werden: ' + (e?.message ?? 'unbekannt'));
				return;
			}

			// Sammeln/Zippen ist fertig — den Fortschritts-Toast wegnehmen, BEVOR der
			// Speichern-Dialog erscheint (sonst klebt die veraltete „wird erstellt"-
			// Meldung hinter dem Datei-Dialog). Im finally bleibt der Clear idempotent.
			if (progress) { this.toastr.clear(progress.toastId); progress = null; }

			// Speicherort waehlen lassen: Desktop zeigt „Speichern unter …"
			// (showSaveFilePicker), Mobil bietet Teilen an, sonst Download-Fallback —
			// alles gekapselt in FileExportService.saveOrShare. Bricht der Nutzer den
			// Dialog ab (AbortError → success:false, error:'Abgebrochen'), gibt es
			// KEINEN Erfolgs-Toast; ein echter Fehler wird gemeldet.
			const saveResult = await this.fileExport.saveOrShare(
				{ filename: fileName, mimeType: 'application/zip', content: blob, suggestedName: fileName },
				'Projekt-Backup'
			);
			if (!saveResult.success) {
				if (saveResult.error && saveResult.error !== 'Abgebrochen') {
					this.toastr.error('Backup konnte nicht gespeichert werden: ' + saveResult.error);
				}
				return;
			}
			// Inhalts-Kennzahlen fuer den Erfolgs-Toast. `project` ist bereits
			// rehydriert (Zeile oben), die Zeichnungs-Features stecken also in
			// `zeichnungslayer[].geojson.features`. Bereiche liegen gewrappt unter
			// `regions.regions` (Regions-Typ). „Layer" ist die Summe ALLER geladenen
			// Karten-Layer (WMS-Overlays + Geodaten-Dateien + Zeichnungslayer +
			// Punkte-Layer) — siehe countLoadedLayers.
			const drawingLayers: any[] = Array.isArray(project.zeichnungslayer) ? project.zeichnungslayer : [];
			const drawingCount = drawingLayers.reduce(
				(sum: number, l: any) => sum + (l?.geojson?.features?.length ?? 0),
				0
			);
			const regionCount = project.regions?.regions?.length ?? 0;
			const pointCount = projectPoints.length;
			const layerCount = countLoadedLayers(project, attManifest.length, pointCount);

			// Ein-/Mehrzahl je Zähler (Layer ist im Deutschen unveränderlich).
			const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
			this.toastr.success(
				`Backup erstellt: ${n(pointCount, 'Punkt', 'Punkte')}, ${n(drawingCount, 'Zeichnung', 'Zeichnungen')}, `
				+ `${n(regionCount, 'Bereich', 'Bereiche')}, ${layerCount} Layer`,
				fileName
			);
		} finally {
			if (progress) this.toastr.clear(progress.toastId);
		}
	}

	async openRestoreWizard(file: File): Promise<string | null> {
		// 1) Datei lesen — ZIP (v2) oder reines JSON (v1/Legacy)
		let text: string;
		let attachments: Record<string, Uint8Array> = {};
		try {
			const buf = new Uint8Array(await file.arrayBuffer());
			if (this.looksLikeZip(buf, file.name)) {
				const entries = unzipSync(buf);
				const jsonEntry = entries['backup.json'];
				if (!jsonEntry) {
					this.toastr.error('Backup-ZIP enthaelt keine backup.json.');
					return null;
				}
				text = strFromU8(jsonEntry);
				for (const [path, data] of Object.entries(entries)) {
					if (path.startsWith('attachments/') && path.length > 'attachments/'.length) {
						attachments[path.slice('attachments/'.length)] = data;
					}
				}
			} else {
				text = strFromU8(buf);
			}
		} catch (e: any) {
			this.toastr.error('Backup-Datei konnte nicht gelesen werden: ' + (e?.message ?? 'unbekannt'));
			return null;
		}

		// 2) Parsen + Format/App-Mismatch hart ablehnen
		const parsed = parseBackup(text, this.appId);
		if (!parsed.ok) {
			this.toastr.error(parsed.error.message);
			return null;
		}
		const backup = parsed.backup;

		// 3) Migrations anwenden (Up- oder Downgrade auf CURRENT_DATA_VERSION)
		let migrated;
		try {
			migrated = applyBackupMigrations(backup, this.currentVersion);
		} catch (e: any) {
			this.toastr.error('Migration des Backups fehlgeschlagen: ' + (e?.message ?? 'unbekannt'));
			return null;
		}

		// 4) Wizard im Restore-Modus oeffnen + Daten reichen. Die Geodaten-Blobs
		// (attachments) werden durchgereicht; der Wizard re-attached sie beim
		// Apply rollenabhaengig (Phase 3).
		const modalRef = this.modalService.open(ImportWizardComponent, { size: 'lg', backdrop: 'static' });
		const wizard = modalRef.componentInstance as ImportWizardComponent;
		await wizard.startRestore(
			backup.project as any,
			migrated.points as AufnahmePunktDocument[],
			migrated.warnings,
			attachments
		);

		// 5) Auf Wizard-Schluss warten
		try {
			await modalRef.result;
		} catch {
			// dismissed (Abbruch) — kein Toast hier, der Wizard zeigt selbst Status
		}

		// 6) Wenn der Wizard tatsaechlich Punkte geschrieben hat, liefern wir
		// die Projekt-ID zurueck — der project-manager macht sie zum aktiven.
		if (wizard.restoredCount > 0) {
			return (backup.project as any)?._id ?? null;
		}
		return null;
	}

	/**
	 * Heuristik: ZIP (v2) vs. reines JSON (v1). Endung `.zip` oder die ZIP-
	 * Magic-Bytes `PK` am Dateianfang reichen.
	 */
	private looksLikeZip(buf: Uint8Array, fileName: string): boolean {
		if (fileName.toLowerCase().endsWith('.zip')) return true;
		return buf.length >= 4 && buf[0] === 0x50 && buf[1] === 0x4B
			&& (buf[2] === 0x03 || buf[2] === 0x05 || buf[2] === 0x07);
	}
}
